import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import OrganizationsIndex from './components/organizations/OrganizationsIndex';
import OrganizationEdit from './components/organizations/OrganizationEdit';
import OrganizationNew from './components/organizations/OrganizationNew';
import TemplateEdit from './components/templates/TemplateEdit';
import TemplateNew from './components/templates/TemplateNew';
import PagesIndex from './components/pages/PagesIndex';
import UsersIndex from './components/users/UsersIndex';
import LoginPage from './components/login/LoginPage';
import ProtectedRoute from './components/ProtectedRoute';
import SuperAdminRoute from './components/SuperAdminRoute';
import RouteGuard from './components/common/RouteGuard';
import { AuthProvider } from './lib/AuthContext';
import { ApiProvider } from './lib/ApiContext';
import Navbar from './components/Navbar';

// NotFound component for 404 errors
function NotFound() {
  useEffect(() => {
    // Set document title
    document.title = "404 - Page Not Found | Exactapage Admin";
  }, []);

  return (
    <div className="container mx-auto p-4 text-center mt-10">
      <h1 className="text-4xl font-bold mb-4">404 - Page Not Found</h1>
      <p className="text-lg mb-6">The page you are looking for doesn't exist or has been moved.</p>
      <a href="/" className="text-blue-500 hover:underline">Go back to home</a>
    </div>
  );
}

// Wrapper component for Routes
function AppRoutes() {
  const location = useLocation();

  // Check if we're redirecting from the 404.html page
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const redirectPath = params.get('redirect');

    if (redirectPath) {
      // If we have a redirect parameter, remove it and navigate to the proper path
      // React Router will handle showing the 404 page if the path doesn't match any routes
      window.history.replaceState(null, '', redirectPath);
    }
  }, []);

  return (
    <>
      <Routes>
        <Route path="/login" element={<LoginPage />} />

        {/* Redirect from root to organizations */}
        <Route path="/" element={<Navigate to="/organizations" replace />} />

        {/* Organization routes - specific routes first */}
        <Route path="/organizations/new" element={
          <SuperAdminRoute>
            <>
              <Navbar />
              <OrganizationNew />
            </>
          </SuperAdminRoute>
        } />

        <Route path="/organizations/:publicId/edit" element={
          <ProtectedRoute>
            <RouteGuard requiresOrganization={true}>
              <Navbar />
              <OrganizationEdit />
            </RouteGuard>
          </ProtectedRoute>
        } />

        <Route path="/organizations" element={
          <ProtectedRoute>
            <>
              <Navbar />
              <OrganizationsIndex />
            </>
          </ProtectedRoute>
        } />

        <Route path="/organizations/:orgPublicId/templates/new" element={
          <SuperAdminRoute>
            <RouteGuard requiresOrganization={true}>
              <Navbar />
              <TemplateNew />
            </RouteGuard>
          </SuperAdminRoute>
        } />

        <Route path="/organizations/:orgPublicId/templates/:templatePublicId/edit" element={
          <SuperAdminRoute>
            <RouteGuard requiresTemplate={true}>
              <Navbar />
              <TemplateEdit />
            </RouteGuard>
          </SuperAdminRoute>
        } />

        <Route path="/organizations/:orgPublicId/templates/:templatePublicId/pages" element={
          <ProtectedRoute>
            <RouteGuard requiresTemplate={true}>
              <Navbar />
              <PagesIndex />
            </RouteGuard>
          </ProtectedRoute>
        } />

        {/* Users routes - specific routes first - SUPER ADMIN ONLY */}
        <Route path="/users/new" element={
          <SuperAdminRoute>
            <>
              <Navbar />
              {/* Replace with UserNew component when created */}
              <div className="container mx-auto p-4">
                <h1 className="text-2xl font-bold">New User</h1>
                <p>New user form will go here.</p>
              </div>
            </>
          </SuperAdminRoute>
        } />

        <Route path="/users/:publicId/edit" element={
          <SuperAdminRoute>
            <>
              <Navbar />
              {/* Replace with UserEdit component when created */}
              <div className="container mx-auto p-4">
                <h1 className="text-2xl font-bold">Edit User</h1>
                <p>User edit form will go here.</p>
              </div>
            </>
          </SuperAdminRoute>
        } />

        <Route path="/users" element={
          <SuperAdminRoute>
            <>
              <Navbar />
              <UsersIndex />
            </>
          </SuperAdminRoute>
        } />

        {/* 404 catch-all route - must be the last route */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <ApiProvider>
        <Router>
          <AppRoutes />
        </Router>
      </ApiProvider>
    </AuthProvider>
  );
}

export default App;
